import React from "react";
import {
  Alert,
  AutoComplete,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import { getContactValues } from "./directory-utils";
import styles from "../company-directory/directory.module.css";

const required = (label) => ({
  required: true,
  whitespace: true,
  message: `${label} is required.`,
});

const templateOptions = (templates = [], current) =>
  [...new Set([...templates, ...(current ? [current] : [])])].map((value) => ({
    value,
    label: value === "Default" ? "Apply Permission Template Later" : value,
    disabled: !templates.includes(value),
  }));

const ContactForm = ({
  editingContact,
  saving,
  error,
  companies,
  permissions,
  permissionsLoading,
  permissionsError,
  onRetryPermissions,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const isEditing = Boolean(editingContact);
  const currentValues = editingContact
    ? getContactValues(editingContact)
    : null;
  const canSubmit = !permissionsLoading && !permissionsError;

  return (
    <Modal
      open
      title={isEditing ? "Edit Contact Info" : "Create Project Contact"}
      width={720}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={isEditing ? "Save Changes" : "Create Contact"}
      confirmLoading={saving}
      okButtonProps={{ disabled: !canSubmit }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={currentValues || {}}
        onFinish={onSubmit}
        disabled={saving}
        requiredMark="optional"
        scrollToFirstError
        style={{ marginTop: 24 }}
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 20 }}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[required("First name")]}
            >
              <Input autoComplete="given-name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[required("Last name")]}
            >
              <Input autoComplete="family-name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeEmail"
              label="Email"
              rules={[
                required("Email"),
                { type: "email", message: "Enter a valid email address." },
              ]}
            >
              <Input type="email" disabled={isEditing} autoComplete="email" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteePhoneNumber"
              label="Phone"
              rules={
                isEditing
                  ? []
                  : [
                      {
                        validator: (_, value) =>
                          !value?.trim() || /^\+?\d{10}$/.test(value.trim())
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  "Phone number must be exactly 10 digits.",
                                ),
                              ),
                      },
                    ]
              }
            >
              <Input type="tel" autoComplete="tel" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeCompany"
              label="Company"
              rules={[required("Company")]}
            >
              <AutoComplete
                options={companies}
                filterOption={(input, option) =>
                  option.value.toLowerCase().includes(input.toLowerCase())
                }
                onSelect={(_, option) =>
                  form.setFieldValue(
                    "inviteeCompanyAddress",
                    option.address || "",
                  )
                }
                placeholder="Select or enter a company"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="inviteeCompanyAddress" label="Company Address">
              <Input autoComplete="street-address" />
            </Form.Item>
          </Col>
        </Row>
        {permissionsError && (
          <Alert
            type="error"
            showIcon
            message={permissionsError}
            action={
              <button
                type="button"
                className={styles.retryLink}
                onClick={onRetryPermissions}
              >
                Retry
              </button>
            }
            style={{ marginBottom: 20 }}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeCompanyPermission"
              label="Company Permissions Template"
              rules={[required("Company permissions template")]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a template"
                loading={permissionsLoading}
                options={templateOptions(
                  permissions.companyAssignablePermissions,
                  currentValues?.inviteeCompanyPermission,
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeProjectPermission"
              label="Project Permissions Template"
              rules={[required("Project permissions template")]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a template"
                loading={permissionsLoading}
                options={templateOptions(
                  permissions.projectAssignablePermissions,
                  currentValues?.inviteeProjectPermission,
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContactForm;
